import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryProvider, ClientConfigProvider } from "@repo/backend/providers";
import { fetchClientConfig } from "@repo/backend/firebase/queries";
import type { ClientConfig } from "@repo/backend/schema/clientConfig";
import { PreviewBanner, AccountPage, PricingPage, SuspendedPage, ThemeToggle, useLenis, G8AttributionBar } from "@repo/features/site";
import { initClientAnalytics } from "@repo/backend/firebase/analytics";
import { HomePage } from "./pages/Home.js";
import { ChatWidget } from "./components/ChatWidget.js";

const CLIENT_ID = "bizzydetails";
const SHOW_THEME_TOGGLE = true;

export default function App() {
  return (
    <QueryProvider>
      <AppLoader />
    </QueryProvider>
  );
}

function AppLoader() {
  useLenis();
  const { data: config, isLoading } = useQuery<ClientConfig>({
    queryKey: ["clientConfig", CLIENT_ID],
    queryFn: () => fetchClientConfig(CLIENT_ID),
    staleTime: Infinity,
    retry: 2,
  });

  useEffect(() => {
    if (config?.analytics?.measurementId) {
      initClientAnalytics(config.analytics.measurementId, config.analytics.appId);
    }
  }, [config?.analytics?.measurementId]);

  if (isLoading) {
    return <div style={{ minHeight: "100vh", background: "var(--color-background, #fff)" }} />;
  }

  if (!config) return null;

  if (window.location.pathname === "/account") {
    return <AccountPage config={config} />;
  }

  if (window.location.pathname === "/pricing") {
    return <PricingPage clientId={CLIENT_ID} config={config} />;
  }

  if (config.status === "suspended") {
    return <SuspendedPage />;
  }

  return (
    <ClientConfigProvider config={config}>
      <PreviewBanner status={config.status} pricingHref={`https://portal.grateful8.us/signup?clientId=${CLIENT_ID}`} />
      {SHOW_THEME_TOGGLE && <ThemeToggle />}
      <HomePage />
      <ChatWidget />
      <G8AttributionBar />
    </ClientConfigProvider>
  );
}
